<template>
  <div class="welcome-bg">
    <v-container class="fill-height d-flex align-center justify-center py-8">
      <v-row justify="center" align="center" class="fill-height">

        <!-- Panel izquierdo: branding e info -->
        <v-col cols="12" md="5" class="d-flex flex-column align-center align-md-start mb-8 mb-md-0 pr-md-8 fade-up">
          <!-- Logos -->
          <div class="logos-row mb-8">
            <img src="/images/logo_colegio.jpg" alt="Logo Colegio" class="logo-main" />
            <div class="logo-divider"></div>
            <img src="/images/logo_afinni.png" alt="Logo Afinni" class="logo-partner" />
          </div>

          <!-- Identidad del instrumento -->
          <div class="instrument-badge mb-4">
            <v-icon small color="accent" class="mr-1">mdi-chart-timeline-variant</v-icon>
            <span>{{ $t('welcome.title') }}</span>
          </div>

          <h1 class="welcome-heading mb-3">
            {{ $t('welcome.subtitle') }}
          </h1>

          <p class="welcome-subtext mb-6">
            {{ $t('welcome.description') }}
          </p>

          <!-- Bloques incluidos -->
          <p class="blocks-title mb-3">{{ $t('framing.blocks_title') }}</p>
          <div class="blocks-list mb-4">
            <div v-for="(block, i) in blocks" :key="i" class="block-item">
              <div class="block-number">{{ String.fromCharCode(65 + i) }}</div>
              <div class="block-content">
                <span class="block-text">{{ block }}</span>
                <span class="block-desc-text">{{ blockDescs[i] }}</span>
              </div>
            </div>
          </div>

        </v-col>

        <!-- Panel derecho: tarjeta de acción -->
        <v-col cols="12" md="4" class="d-flex justify-center fade-up delay-1">
          <v-card class="action-card pa-8" elevation="0">

            <!-- Saludo personalizado -->
            <div class="greeting-section mb-6">
              <p class="greeting-label">{{ $t('welcome.hello') }}</p>
              <h2 class="greeting-name">{{ teacherName }}</h2>
            </div>

            <v-divider class="mb-6"></v-divider>

            <!-- Info del ejercicio -->
            <div class="info-row mb-3">
              <v-icon small color="primary" class="mr-2">mdi-clock-outline</v-icon>
              <span>{{ $t('welcome.duration') }}</span>
            </div>
            <p class="exercise-desc mb-6">{{ $t('welcome.exercise_desc') }}</p>

            <!-- Mensaje -->
            <p class="motivation-text mb-7">
              "{{ $t('welcome.motivation') }}"
            </p>

            <!-- CTA -->
            <v-btn
              block
              large
              color="primary"
              class="start-btn"
              @click="goToMain"
              elevation="0"
            >
              {{ $t('welcome.start') }}
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>

          </v-card>
        </v-col>

      </v-row>
    </v-container>
  </div>
</template>

<script>
import { useMainStore } from '@/store/main';
import { mapStores } from 'pinia';

export default {
  name: 'WelcomePage',
  computed: {
    ...mapStores(useMainStore),
    teacherName() {
      return this.mainStore.logged_user.name || '';
    },
    blocks() {
      return [
        this.$t('welcome.block_1'),
        this.$t('welcome.block_2'),
        this.$t('welcome.block_3'),
        this.$t('welcome.block_4'),
        this.$t('welcome.block_5'),
        this.$t('welcome.block_6'),
      ];
    },
    blockDescs() {
      return [
        this.$t('welcome.block_1_desc'),
        this.$t('welcome.block_2_desc'),
        this.$t('welcome.block_3_desc'),
        this.$t('welcome.block_4_desc'),
        this.$t('welcome.block_5_desc'),
        this.$t('welcome.block_6_desc'),
      ];
    }
  },
  methods: {
    goToMain() {
      this.mainStore.goToBlock(1);
      this.$router.replace({ name: 'TeacherSurvey' });
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap');

/* ── Fondo ─────────────────────────────────────────── */
.welcome-bg {
  min-height: 100vh;
  background: #f8f9fb;
  position: relative;
}

/* ── Logos ─────────────────────────────────────────── */
.logos-row {
  display: flex;
  align-items: center;
  gap: 20px;
}
.logo-main {
  height: 64px;
  width: auto;
  border-radius: 10px;
  object-fit: contain;
}
.logo-partner {
  height: 38px;
  width: auto;
  opacity: 0.85;
}
.logo-divider {
  width: 1px;
  height: 40px;
  background: #cbd5e0;
}

/* ── Badge instrumento ─────────────────────────────── */
.instrument-badge {
  display: inline-flex;
  align-items: center;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #1e40af;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* ── Tipografía izquierda ───────────────────────────── */
.welcome-heading {
  font-size: 2rem;
  font-weight: 700;
  color: #1e3a5f;
  line-height: 1.25;
  font-family: 'Quicksand', sans-serif;
}

.welcome-subtext {
  font-size: 1rem;
  color: #4a5568;
  line-height: 1.6;
  max-width: 380px;
}

/* ── Lista de bloques ───────────────────────────────── */
.blocks-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #1e3a5f;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.voice-note {
  font-size: 0.85rem;
  color: #4a5568;
  font-style: italic;
  line-height: 1.5;
  max-width: 380px;
  border-left: 3px solid #c2853e;
  padding-left: 10px;
}

.blocks-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 380px;
}

.block-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.block-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.block-number {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background: #1e3a5f;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.block-text {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1e3a5f;
  line-height: 1.4;
}

.block-desc-text {
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.4;
}

/* ── Nota confidencialidad ─────────────────────────── */
.confidentiality-note {
  display: flex;
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.82rem;
  color: #166534;
  max-width: 380px;
}

/* ── Tarjeta de acción ──────────────────────────────── */
.action-card {
  width: 100%;
  max-width: 360px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 4px 24px rgba(30, 58, 95, 0.08), 0 1px 4px rgba(0,0,0,0.04);
}

/* ── Saludo ─────────────────────────────────────────── */
.greeting-section {
  text-align: center;
}
.greeting-label {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 4px;
  font-weight: 500;
}
.greeting-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e3a5f;
  font-family: 'Quicksand', sans-serif;
}

/* ── Info rows ──────────────────────────────────────── */
.info-row {
  display: flex;
  align-items: center;
  font-size: 0.88rem;
  color: #4a5568;
}

.exercise-desc {
  font-size: 0.82rem;
  color: #4a5568;
  line-height: 1.55;
  border-left: 3px solid #1e3a5f;
  padding-left: 10px;
  margin-left: 2px;
}

/* ── Motivación ─────────────────────────────────────── */
.motivation-text {
  font-size: 0.88rem;
  color: #6b7280;
  font-style: italic;
  line-height: 1.55;
  border-left: 3px solid #c2853e;
  padding-left: 12px;
}

/* ── Botón CTA ──────────────────────────────────────── */
.start-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  font-size: 0.95rem !important;
  letter-spacing: 0.02em !important;
  transition: all 0.2s ease !important;
}
.start-btn:hover {
  opacity: 0.92;
  transform: translateY(-1px);
}

/* ── Animaciones de entrada ─────────────────────────── */
.fade-up {
  animation: fadeUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.delay-1 { animation-delay: 0.12s; }

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── Responsive ─────────────────────────────────────── */
@media (max-width: 960px) {
  .welcome-heading { font-size: 1.6rem; }
  .logos-row { justify-content: center; }
  .blocks-list, .confidentiality-note { max-width: 100%; }
  .welcome-subtext { max-width: 100%; }
  .action-card { max-width: 100%; }
}
</style>
